import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, GridBag, Headline } from '@gaiads/telia-react-component-library';
import { PageInfo } from '@cdaas/api-clients/dist/asset-user-openapi';
import { AssetResponseWithAutoOpen } from '@cdaas/reusable-components-daas/dist/src/asset-api-types/api-types';
import { downloadSpreadsheet, FilterOption, PageChangeInfo, SearchAndFilter, SearchAndFilterOnChange } from '@cdaas/reusable-components-daas';

import OrganizationAssetsTable from './OrganizationAssetsTable';
import { RootState } from '../../store/reducers';
import {
  getOrganizationAssetReport,
  getOrganizationAssets as getOrganizationAssetsApi,
  GetOrganizationAssetsSearchParams,
  getQueryParams,
  updateQueryParams,
} from '../../utils';
import { showGrowlNotification } from '../../store/actions';
import { DEFAULT_PAGE_SIZE } from '../../utils/constants';

const pageBasePath = '/organizationassets';

enum AssetFilter {
  ActiveAssets = 'active',
  InactiveAssets = 'inactive',
  AllAssets = 'all',
}

const SearchScopeBaseEnum = {
  Name: 'Name',
  PhoneNumber: 'PhoneNumber',
  Email: 'Email',
  Imei: 'Imei',
  LocationReference: 'LocationReference',
  OrderId: 'OrderId',
} as const;

type SearchScope = (typeof SearchScopeBaseEnum)[keyof typeof SearchScopeBaseEnum];

const OrganizationAssetsList: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('assets');
  const location = useLocation();

  const [assets, setAssets] = useState<AssetResponseWithAutoOpen[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>();
  const [loading, setLoading] = useState(true);
  const filterAssetsWaitingForReturnEnabled = useSelector(
    (state: RootState) => state.featureFlags.FEATURE_FLAG_FILTER_ASSETS_WAITING_FOR_RETURN_ENABLED,
  );

  const organizationName = useSelector((state: RootState) => state.authentication.user?.organization?.name);

  const getSearchScopeOption = (searchScope: SearchScope) => {
    switch (searchScope) {
      case SearchScopeBaseEnum.Name:
        return {
          value: SearchScopeBaseEnum.Name,
          label: t('OrganizationAssets.searchAssets.scope.name'),
          placeHolder: t('OrganizationAssets.searchAssets.scope.namePlaceholder'),
        };
      case SearchScopeBaseEnum.PhoneNumber:
        return {
          value: SearchScopeBaseEnum.PhoneNumber,
          label: t('OrganizationAssets.searchAssets.scope.phoneNumber'),
          placeHolder: t('OrganizationAssets.searchAssets.scope.phoneNumberPlaceholder'),
        };
      case SearchScopeBaseEnum.Email:
        return {
          value: SearchScopeBaseEnum.Email,
          label: t('OrganizationAssets.searchAssets.scope.email'),
          placeHolder: t('OrganizationAssets.searchAssets.scope.emailPlaceholder'),
        };
      case SearchScopeBaseEnum.Imei:
        return {
          value: SearchScopeBaseEnum.Imei,
          label: t('OrganizationAssets.searchAssets.scope.imei'),
          placeHolder: t('OrganizationAssets.searchAssets.scope.imeiPlaceholder'),
        };
      case SearchScopeBaseEnum.OrderId:
        return {
          value: SearchScopeBaseEnum.OrderId,
          label: t('OrganizationAssets.searchAssets.scope.orderId'),
          placeHolder: t('OrganizationAssets.searchAssets.scope.orderIdPlaceholder'),
        };
      case SearchScopeBaseEnum.LocationReference:
        return {
          value: SearchScopeBaseEnum.LocationReference,
          label: t('OrganizationAssets.searchAssets.scope.locationReference'),
          placeHolder: t('OrganizationAssets.searchAssets.scope.locationReferencePlaceholder'),
        };
    }
  };

  const filters: FilterOption[] = [
    {
      label: t('OrganizationAssets.searchAssets.filters.active'),
      value: AssetFilter.ActiveAssets,
    },
    {
      label: t('OrganizationAssets.searchAssets.filters.inactive'),
      value: AssetFilter.InactiveAssets,
    },
    {
      label: t('OrganizationAssets.searchAssets.filters.all'),
      value: AssetFilter.AllAssets,
    },
  ];

  const [searchOptions, setSearchOptions] = useState<SearchAndFilterOnChange>();

  const getOrganizationAssets = async (
    searchScope: SearchScope,
    searchText: string,
    assetActivityFilter: AssetFilter,
    filterEndOfLeaseAssets: boolean,
    filterWaitingForReturnAssets: boolean,
    openAutomaticallyWithId?: string,
    page?: number,
  ) => {
    const constructSearchParams = (
      searchScope: SearchScope,
      searchText: string,
      filterEndOfLeaseAssets: boolean,
      filterWaitingForReturnAssets: boolean,
    ): GetOrganizationAssetsSearchParams => ({
      ...(searchText && searchScope === SearchScopeBaseEnum.Name && { name: searchText }),
      ...(searchText && searchScope === SearchScopeBaseEnum.PhoneNumber && { phoneNumber: searchText }),
      ...(searchText && searchScope === SearchScopeBaseEnum.Email && { email: searchText }),
      ...(searchText && searchScope === SearchScopeBaseEnum.Imei && { imei: searchText }),
      ...(searchText && searchScope === SearchScopeBaseEnum.LocationReference && { locationReference: searchText }),
      ...(searchText && searchScope === SearchScopeBaseEnum.OrderId && { orderId: searchText }),
      ...(assetActivityFilter !== AssetFilter.AllAssets && { state: assetActivityFilter }),
      waitingForReturn: filterWaitingForReturnAssets,
      withEndOfLeaseOptions: filterEndOfLeaseAssets,
    });

    setLoading(true);
    const res = await getOrganizationAssetsApi(
      DEFAULT_PAGE_SIZE,
      page,
      constructSearchParams(searchScope, searchText, filterEndOfLeaseAssets, filterWaitingForReturnAssets),
    );
    if (res?.contents) {
      setAssets(res?.contents.map(asset => ({ asset: asset, autoOpen: asset.id === openAutomaticallyWithId })));
      setPageInfo(res?.page_metadata);
    } else {
      dispatch(showGrowlNotification(t('getOrganizationDevicesError'), 'error'));
    }
    setLoading(false);
  };

  const handlePageChange = (pageChangeInfo: PageChangeInfo<AssetResponseWithAutoOpen>) => {
    updateQueryParams(
      pageBasePath,
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        scope: searchOptions!.search?.scope?.value!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        search: searchOptions!.search?.value!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        filter: searchOptions!.filters?.[0].value!,
        eol_only: searchOptions!.checkboxStates![0],
        waiting_for_return_only: searchOptions!.checkboxStates![1],
        page: pageChangeInfo.nextChosenPageNumber,
      },
      'replace',
    );
  };

  useEffect(() => {
    const params = getQueryParams(new URLSearchParams(location.search), [
      {
        name: 'scope',
        type: 'string',
        allowedValues: Object.values(SearchScopeBaseEnum),
        defaultValue: SearchScopeBaseEnum.Name,
      },
      {
        name: 'search',
        type: 'string',
        defaultValue: '',
      },
      {
        name: 'filter',
        type: 'string',
        allowedValues: Object.values(AssetFilter),
        defaultValue: AssetFilter.AllAssets,
      },
      {
        name: 'eol_only',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'waiting_for_return_only',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'page',
        type: 'number',
        defaultValue: 0,
      },
    ]);
    const filterToUse: FilterOption = { ...filters.find(filter => filter.value === (params.filter as string))!, checked: true };
    setSearchOptions({
      filters: [filterToUse],
      search: { value: params.search as string, scope: getSearchScopeOption(params.scope as SearchScope) },
      checkboxStates: [params.eol_only as boolean, params.waiting_for_return_only as boolean],
    });
    getOrganizationAssets(
      params.scope as SearchScope,
      params.search as string,
      params.filter as AssetFilter,
      params.eol_only as boolean,
      params.waiting_for_return_only as boolean,
      undefined,
      +params.page,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleDownloadReport = async (): Promise<void> => {
    const response = await getOrganizationAssetReport();
    if (response === null) {
      dispatch(showGrowlNotification(t('getOrganizationAssetReportError'), 'error'));
    } else {
      const filePrefix = organizationName ? `${organizationName}-devices` : 'devices';
      await downloadSpreadsheet('Organization devices', response, filePrefix);
    }
  };

  const handleSearchChange = (values: SearchAndFilterOnChange) => {
    setSearchOptions(values);
    updateQueryParams(
      pageBasePath,
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        scope: values.search?.scope?.value!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        search: values.search?.value!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        filter: values.filters?.[0].value!,
        eol_only: values.checkboxStates![0],
        waiting_for_return_only: filterAssetsWaitingForReturnEnabled && values.checkboxStates![1],
        page: 0,
      },
      'replace',
    );
  };

  const currentSearchScope = searchOptions && getSearchScopeOption(searchOptions.search?.scope?.value as SearchScope);

  return (
    <>
      <Headline size={'lg'} margin={{ vertical: 'xlg' }}>
        {t('OrganizationAssets.organizationAssetsTitle')}
      </Headline>
      <GridBag noVerticalGutter>
        <GridBag.Item md={8} padding={{ right: 'sm' }}>
          {!!searchOptions && (
            <SearchAndFilter
              searchConfig={{
                useDebounceInTextSearch: 1000,
                searchInputOrSearchInputWithScopeProps: {
                  value: searchOptions.search?.value,
                  label: t('OrganizationAssets.searchAssets.searchLabel'),
                  placeholder: currentSearchScope?.placeHolder,
                  scopeOptions: Object.values(SearchScopeBaseEnum).map(searchScope => getSearchScopeOption(searchScope)),
                  forceUpdateValue: searchOptions.search?.value,
                  forceUpdateScope: searchOptions.search?.scope,
                },
              }}
              filterConfig={{
                text: t('OrganizationAssets.searchAssets.filters.filterActivityLabel'),
                filters,
                radioSelect: true,
                forceUpdateFilters: filters.map(filter => ({ ...filter, checked: filter.value === searchOptions.filters?.[0].value })),
              }}
              onChangeCallback={handleSearchChange}
              checkboxConfigs={[
                {
                  label: t('OrganizationAssets.searchAssets.filters.filterEndOfLeaseLabel'),
                  checked: searchOptions.checkboxStates![0],
                  forceUpdateValue: searchOptions.checkboxStates![0],
                },
                ...(filterAssetsWaitingForReturnEnabled
                  ? [
                      {
                        label: t('OrganizationAssets.searchAssets.filters.filterWaitingForReturnLabel'),
                        checked: searchOptions.checkboxStates![1],
                        forceUpdateValue: searchOptions.checkboxStates![1],
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </GridBag.Item>
        <GridBag.Item md={4} alignTextToRight padding={{ top: 'sm' }}>
          <Button variant="secondary" iconName="download" iconOnRight onClick={handleDownloadReport}>
            {t('OrganizationAssets.downloadReportButton')}
          </Button>
        </GridBag.Item>
      </GridBag>
      <OrganizationAssetsTable loading={loading} assets={assets} pageInfo={pageInfo} onPageChange={handlePageChange} />
    </>
  );
};

export default OrganizationAssetsList;
